import { useId } from "react";

import type { HistoryPoint } from "./types";

interface TrendChartProps {
  points: HistoryPoint[];
  label: string;
}

const WIDTH = 880;
const HEIGHT = 290;
const PADDING = { top: 20, right: 22, bottom: 42, left: 54 };

function readableDate(value: string): string {
  return new Intl.DateTimeFormat("en-ZA", { day: "numeric", month: "short" }).format(
    new Date(`${value}T00:00:00Z`),
  );
}

export function TrendChart({ points, label }: TrendChartProps) {
  const gradientId = useId().replaceAll(":", "");
  if (points.length < 2) {
    return <div className="chart-empty">Trend data will appear after two daily reports.</div>;
  }

  const values = points.map((point) => point.recoveryCents);
  const rawMin = Math.min(...values, 0);
  const rawMax = Math.max(...values, 0);
  const spread = Math.max(rawMax - rawMin, 20);
  const min = rawMin - spread * 0.12;
  const max = rawMax + spread * 0.12;
  const plotWidth = WIDTH - PADDING.left - PADDING.right;
  const plotHeight = HEIGHT - PADDING.top - PADDING.bottom;
  const x = (index: number) => PADDING.left + (index / (points.length - 1)) * plotWidth;
  const y = (value: number) => PADDING.top + ((max - value) / (max - min)) * plotHeight;
  const line = points
    .map((point, index) => `${index === 0 ? "M" : "L"}${x(index)},${y(point.recoveryCents)}`)
    .join(" ");
  const area = `${line} L${x(points.length - 1)},${PADDING.top + plotHeight} L${x(0)},${
    PADDING.top + plotHeight
  } Z`;
  const ticks = [max, (max + min) / 2, min];
  const dateIndexes = [0, Math.floor((points.length - 1) / 2), points.length - 1];

  return (
    <div className="chart-wrap">
      <svg
        className="trend-chart"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="img"
        aria-label={`${label} average recovery trend in cents per litre`}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#2cad79" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#2cad79" stopOpacity="0.01" />
          </linearGradient>
        </defs>
        {ticks.map((tick) => (
          <g key={tick}>
            <line
              x1={PADDING.left}
              x2={WIDTH - PADDING.right}
              y1={y(tick)}
              y2={y(tick)}
              className="chart-grid"
            />
            <text x={PADDING.left - 10} y={y(tick) + 4} className="chart-y-label">
              {Math.round(tick)}c
            </text>
          </g>
        ))}
        {min < 0 && max > 0 ? (
          <line
            x1={PADDING.left}
            x2={WIDTH - PADDING.right}
            y1={y(0)}
            y2={y(0)}
            className="chart-zero"
          />
        ) : null}
        <path d={area} fill={`url(#${gradientId})`} />
        <path d={line} className="chart-line" />
        {dateIndexes.map((index) => (
          <text key={index} x={x(index)} y={HEIGHT - 12} className="chart-date">
            {readableDate(points[index]?.date ?? "")}
          </text>
        ))}
        <circle
          cx={x(points.length - 1)}
          cy={y(points.at(-1)?.recoveryCents ?? 0)}
          r="5"
          className="chart-dot"
        />
      </svg>
    </div>
  );
}
